/**
 * @registry Workflow lifecycle statuses and their display colors. Mechanism-level (set by the backend/useWorkflowSubmission for step-less transitions), not per-workflow config. Use to resolve a status color when no workflow step applies.
 */

// These status names are workflow-mechanism constants, mirroring the 'Voided'
// literal in useWorkflowSubmission and Draft/Rejected in the backend workflow
// helpers — they are not derived from per-job workflow configuration.
export const WORKFLOW_LIFECYCLE_STATUSES = [
  'Draft',
  'Rejected',
  'Voided',
] as const;

type WorkflowLifecycleStatus = typeof WORKFLOW_LIFECYCLE_STATUSES[number];

const WORKFLOW_LIFECYCLE_COLORS: Record<WorkflowLifecycleStatus, string> = {
  Draft: 'gray',
  Rejected: 'red',
  Voided: 'gray',
};

export const isWorkflowLifecycleStatus = (
  status: string | null | undefined,
): status is WorkflowLifecycleStatus =>
  status != null &&
  (WORKFLOW_LIFECYCLE_STATUSES as readonly string[]).includes(status);

export const resolveLifecycleColor = (
  status: string | null | undefined,
): string | undefined =>
  isWorkflowLifecycleStatus(status)
    ? WORKFLOW_LIFECYCLE_COLORS[status]
    : undefined;

const TERMINAL_STATUSES = ['Voided', 'Rejected'] as const;

/**
 * @registry True when a workflow record is in a terminal state (Voided or Rejected)
 * and its action buttons should be hidden. RFI is excluded — its reject returns to Draft.
 */
export const isTerminalStatus = (status: string | null | undefined): boolean =>
  status != null && (TERMINAL_STATUSES as readonly string[]).includes(status);
